import { useEffect, useState } from "react";

// Measured width of the chart's own box, so tick density can be chosen from the
// space actually available. Breakpoints alone are not enough: the same viewport
// yields very different chart widths across a full-width card, a dashboard grid
// cell, or a viewport narrowed by open dev tools.
//
// `ref` is a callback ref held in state, not a useRef, and that is the whole
// point: several callers only render the measured box once their data has
// arrived. With a ref object the effect runs once on mount, finds `ref.current`
// still null, and never runs again — the observer is never attached, `width`
// stays 0, and a chart gated on `width > 0` renders nothing, for good. Keying
// the effect on the element means it attaches whenever the box appears and
// re-attaches if it is swapped.
export const useChartWidth = () => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured) setWidth(measured);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref: setElement, width };
};
